import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, Pencil, X, Check, Activity } from 'lucide-react';
import { apiFetch } from '@/lib/apiFetch';

interface Connection {
  id: string;
  engine_type: string;
  display_name: string;
  base_url: string;
  settings: Record<string, unknown>;
  enabled: boolean;
  org_id: string | null;
  created_at: string;
}

const EMPTY_FORM = {
  engine_type: 'airia',
  display_name: '',
  base_url: '',
  api_key: '', // credentials.api_key — write-only
  settings: '{}',
  org_scope: 'platform' as 'platform' | 'org',
};

export const EngineConnectionsPanel: React.FC = () => {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<
    Record<string, { status: string; message: string }>
  >({});
  const [testing, setTesting] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/engine-connections');
      const d = await res.json();
      setConnections(d?.data ?? []);
    } catch {
      /* silent */
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setForm({ ...EMPTY_FORM });
    setEditingId(null);
    setShowCreate(true);
    setError(null);
  };
  const openEdit = (c: Connection) => {
    setForm({
      engine_type: c.engine_type,
      display_name: c.display_name,
      base_url: c.base_url,
      api_key: '', // never pre-filled — write-only
      settings: JSON.stringify(c.settings ?? {}, null, 2),
      org_scope: c.org_id ? 'org' : 'platform',
    });
    setEditingId(c.id);
    setShowCreate(false);
    setError(null);
  };

  const save = async () => {
    setError(null);
    let settingsParsed: Record<string, unknown> = {};
    try {
      settingsParsed = JSON.parse(form.settings || '{}');
    } catch {
      setError('Settings must be valid JSON');
      return;
    }

    setSaving(true);
    try {
      const url = editingId
        ? `/api/engine-connections/${editingId}`
        : '/api/engine-connections';
      const method = editingId ? 'PATCH' : 'POST';
      const body: Record<string, unknown> = {
        engine_type: form.engine_type,
        display_name: form.display_name,
        base_url: form.base_url,
        settings: settingsParsed,
        org_scope: form.org_scope,
      };
      if (form.api_key) body.credentials = { api_key: form.api_key };

      const res = await apiFetch(url, {
        method,
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const d = await res.json();
        setError(d.error ?? 'Save failed');
        return;
      }
      setShowCreate(false);
      setEditingId(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    }
    setSaving(false);
  };

  const testConn = async (id: string) => {
    setTesting(id);
    try {
      const res = await apiFetch(`/api/engine-connections/${id}/test`, {
        method: 'POST',
      });
      const d = await res.json();
      setTestResults((prev) => ({
        ...prev,
        [id]: { status: d.status, message: d.message },
      }));
    } catch (e) {
      setTestResults((prev) => ({
        ...prev,
        [id]: {
          status: 'error',
          message: e instanceof Error ? e.message : 'Test failed',
        },
      }));
    }
    setTesting(null);
  };

  const FormPanel = () => (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">
            {editingId ? 'Edit Connection' : 'New Connection'}
          </CardTitle>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              setShowCreate(false);
              setEditingId(null);
            }}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label>Engine Type</Label>
            <Input
              value={form.engine_type}
              onChange={(e) =>
                setForm((p) => ({ ...p, engine_type: e.target.value }))
              }
              placeholder="airia"
            />
          </div>
          <div className="space-y-1">
            <Label>Display Name *</Label>
            <Input
              value={form.display_name}
              onChange={(e) =>
                setForm((p) => ({ ...p, display_name: e.target.value }))
              }
              placeholder="Acme County Airia"
            />
          </div>
        </div>
        <div className="space-y-1">
          <Label>Base URL *</Label>
          <Input
            value={form.base_url}
            onChange={(e) =>
              setForm((p) => ({ ...p, base_url: e.target.value }))
            }
            placeholder="https://api.airia.ai"
          />
        </div>
        <div className="space-y-1">
          <Label>
            API Key {editingId ? '(leave blank to keep existing)' : '*'}
          </Label>
          <Input
            type="password"
            value={form.api_key}
            onChange={(e) =>
              setForm((p) => ({ ...p, api_key: e.target.value }))
            }
            placeholder="sk-…"
            autoComplete="new-password"
          />
          <p className="text-xs text-gray-400">
            Write-only — never retrieved after saving.
          </p>
        </div>
        <div className="space-y-1">
          <Label>Scope</Label>
          <select
            className="w-full border rounded px-3 py-1.5 text-sm"
            value={form.org_scope}
            onChange={(e) =>
              setForm((p) => ({
                ...p,
                org_scope: e.target.value as 'platform' | 'org',
              }))
            }
          >
            <option value="platform">Platform-wide</option>
            <option value="org">My Organization</option>
          </select>
        </div>
        <div className="space-y-1">
          <Label>Settings (JSON)</Label>
          <Textarea
            rows={3}
            className="font-mono text-xs"
            value={form.settings}
            onChange={(e) =>
              setForm((p) => ({ ...p, settings: e.target.value }))
            }
          />
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex gap-2">
          <Button onClick={save} disabled={saving}>
            <Check className="w-4 h-4 mr-1" />
            {saving ? 'Saving…' : 'Save'}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setShowCreate(false);
              setEditingId(null);
            }}
          >
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Engine Connections</h2>
        <Button size="sm" onClick={openCreate}>
          <Plus className="w-4 h-4 mr-1" />
          New Connection
        </Button>
      </div>

      {(showCreate || editingId) && <FormPanel />}

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-10 bg-gray-100 animate-pulse rounded" />
          ))}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Base URL</TableHead>
              <TableHead>Scope</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Health</TableHead>
              <TableHead className="w-16" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {connections.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center text-gray-400 py-8"
                >
                  No connections yet.
                </TableCell>
              </TableRow>
            )}
            {connections.map((c) => {
              const tr = testResults[c.id];
              return (
                <TableRow
                  key={c.id}
                  className={editingId === c.id ? 'bg-blue-50' : ''}
                >
                  <TableCell className="font-medium">
                    {c.display_name}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{c.engine_type}</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-gray-500 truncate max-w-40">
                    {c.base_url}
                  </TableCell>
                  <TableCell>{c.org_id ? 'org' : 'platform'}</TableCell>
                  <TableCell>
                    <Badge variant={c.enabled ? 'default' : 'destructive'}>
                      {c.enabled ? 'enabled' : 'disabled'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {tr ? (
                      <span
                        className={`text-xs ${tr.status === 'ok' ? 'text-green-600' : 'text-red-600'}`}
                      >
                        {tr.status}: {tr.message}
                      </span>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={testing === c.id}
                        onClick={() => testConn(c.id)}
                      >
                        <Activity className="w-3 h-3 mr-1" />
                        {testing === c.id ? 'Testing…' : 'Test'}
                      </Button>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEdit(c)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
};
